import { useState } from "react";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { COLOR_HEX, slug } from "@/lib/api";
import { useKit } from "@/context/KitContext";

const Swatches = ({ p, color, setColor }) => (
  <div className="flex flex-wrap gap-2" data-testid={`color-options-${p.id}`}>
    {p.colors.map((c) => (
      <button
        key={c} type="button" title={c} onClick={() => setColor(c)}
        data-testid={`color-${p.id}-${slug(c)}`}
        className={`w-8 h-8 rounded-full border grid place-items-center transition-transform hover:scale-110 ${
          color === c ? "ring-2 ring-offset-2 ring-slate-900" : "border-slate-300"
        }`}
        style={{ background: COLOR_HEX[c] || c.toLowerCase().replace(/\s+/g, "") }}
      >
        {color === c && <Check className={`w-3.5 h-3.5 ${c === "White" ? "text-slate-900" : "text-white"}`} />}
      </button>
    ))}
  </div>
);

const Sizes = ({ p, size, setSize }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`size-options-${p.id}`}>
    {p.sizes.map((s) => (
      <button
        key={s} type="button" onClick={() => setSize(s)} data-testid={`size-${p.id}-${slug(s)}`}
        className={`min-w-[2.5rem] h-9 px-2 rounded-lg text-sm font-mono border transition-colors ${
          size === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"
        }`}
      >
        {s}
      </button>
    ))}
  </div>
);

export const ProductCard = ({ p, maxItems, budget }) => {
  const { items, add, total } = useKit();
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const full = items.length >= maxItems;
  const tooExpensive = total + p.points > budget;
  const needsPick = (p.colors.length && !color) || (p.sizes.length && !size);

  const onAdd = () => {
    add({ product_id: p.id, name: p.name, image: p.image, points: p.points, color, size });
    toast.success(`${p.name} added to your kit`);
    setSize(null);
  };

  const label = full ? "Kit is full" : tooExpensive ? "Not enough points" : needsPick ? `Pick ${!color && p.colors.length ? "a color" : "a size"}` : "Add to kit";

  return (
    <article className="group bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col transition-shadow hover:shadow-lg" data-testid={`product-card-${p.id}`}>
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-6 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{p.category}</p>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">{p.name}</h3>
          </div>
          <span className="shrink-0 font-mono text-sm font-semibold px-2.5 py-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8]" data-testid={`product-points-${p.id}`}>
            {p.points} pts
          </span>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">{p.description}</p>
        {p.colors.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Color: <span className="text-slate-900 font-medium">{color || "—"}</span></p>
            <Swatches p={p} color={color} setColor={setColor} />
          </div>
        )}
        {p.sizes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Size</p>
            <Sizes p={p} size={size} setSize={setSize} />
          </div>
        )}
        <button
          onClick={onAdd} disabled={full || tooExpensive || needsPick} data-testid={`add-to-kit-${p.id}`}
          className="mt-auto h-11 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-[#1A73E8] active:scale-[0.98] disabled:bg-slate-200 disabled:text-slate-500"
        >
          {!(full || tooExpensive || needsPick) && <Plus className="w-4 h-4" />} {label}
        </button>
      </div>
    </article>
  );
};
